<template>
    <Head title="Edit SLA Policy" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto py-6">
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow rounded-lg">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit SLA Policy</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Update the service level agreement policy settings.</p>
                        </div>
                        <InfoButton title="Editing SLA Policies" variant="default">
                            <!-- INTERNAL NOTE: This feature is being updated. This InfoButton content will need to be revised when the SLA feature update is complete. -->
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                Edit an existing SLA Policy to adjust its matching criteria or time targets. Changes to SLA policies
                                only affect new work orders created after the update - existing work orders retain their original SLA
                                targets. This allows you to refine SLA policies based on experience without disrupting active work.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How changes affect work orders</strong><br>
                                • <strong>Existing work orders:</strong> Not affected - retain original SLA times even if policy
                                  changes<br>
                                • <strong>New work orders:</strong> Will use updated SLA targets immediately after saving<br>
                                • <strong>Criteria changes:</strong> May cause different work orders to match this policy<br>
                                • <strong>Time adjustments:</strong> Updated response/resolution times apply to all future matching
                                  work orders<br>
                                • <strong>Deactivation:</strong> Prevents this policy from matching any new work orders
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Review work order count</strong> - Shows how many work orders have used this policy<br>
                                • <strong>Adjust based on reality</strong> - If SLAs are consistently missed, consider extending
                                  targets<br>
                                • <strong>Warning indicator</strong> - Yellow alert shows when policy is actively used<br>
                                • <strong>Test after editing</strong> - Create a sample work order to verify policy still matches
                                  correctly<br>
                                • <strong>Document changes</strong> - Update description to note why adjustments were made
                            </p>
                        </InfoButton>
                    </div>
                </div>

                <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                    <!-- Name Field -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300 dark:border-red-500': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.name }}
                        </p>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300 dark:border-red-500': form.errors.description }"
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.description }}
                        </p>
                    </div>

                    <!-- Criteria Section -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Leave criteria blank to apply to all work orders of that type. More specific policies will take precedence.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Work Order Type -->
                            <div>
                                <label for="work_order_type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Work Order Type
                                </label>
                                <select
                                    id="work_order_type"
                                    v-model="form.work_order_type"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.work_order_type }"
                                >
                                    <option value="">All Types</option>
                                    <option value="corrective">Corrective</option>
                                    <option value="preventive">Preventive</option>
                                    <option value="predictive">Predictive</option>
                                    <option value="inspection">Inspection</option>
                                    <option value="calibration">Calibration</option>
                                    <option value="emergency">Emergency</option>
                                </select>
                                <p v-if="form.errors.work_order_type" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.work_order_type }}
                                </p>
                            </div>

                            <!-- Priority -->
                            <div>
                                <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    v-model="form.priority"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.priority }"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.priority }}
                                </p>
                            </div>

                            <!-- Service Category -->
                            <div>
                                <label for="service_category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service Category
                                </label>
                                <select
                                    id="service_category_id"
                                    v-model="form.service_category_id"
                                    @change="onCategoryChange"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.service_category_id }"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="category in serviceCategories" :key="category.id" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_category_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.service_category_id }}
                                </p>
                            </div>

                            <!-- Service -->
                            <div>
                                <label for="service_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service
                                </label>
                                <select
                                    id="service_id"
                                    v-model="form.service_id"
                                    :disabled="!form.service_category_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.service_id }"
                                >
                                    <option value="">All Services</option>
                                    <option v-for="service in availableServices" :key="service.id" :value="service.id">
                                        {{ service.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.service_id }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- SLA Times Section -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">SLA Time Targets</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Define time limits in hours for response and resolution.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Response Time -->
                            <div>
                                <label for="response_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Response Time (hours)
                                </label>
                                <input
                                    id="response_time_hours"
                                    v-model="form.response_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 4.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.response_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Time until work order must be acknowledged/started</p>
                                <p v-if="form.errors.response_time_hours" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.response_time_hours }}
                                </p>
                            </div>

                            <!-- Resolution Time -->
                            <div>
                                <label for="resolution_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Resolution Time (hours)
                                </label>
                                <input
                                    id="resolution_time_hours"
                                    v-model="form.resolution_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 24.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.resolution_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Time until work order must be completed</p>
                                <p v-if="form.errors.resolution_time_hours" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.resolution_time_hours }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Is Active Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                            <p class="text-gray-500 dark:text-gray-400">Make this policy available for matching.</p>
                        </div>
                    </div>

                    <!-- Warning if Policy has Work Orders -->
                    <div v-if="policy.work_orders_count > 0" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-yellow-700">
                                    <strong>Warning:</strong> This policy is currently used by {{ policy.work_orders_count }} work order(s). Changes will not affect existing work orders, only new ones.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Info Box -->
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700">
                                    <strong>Specificity Matching:</strong> Policies with more specific criteria (service > category > type/priority) will be matched first. For example, a policy for "Electrical > Power Outage > Urgent" will be used instead of a general "Urgent" policy.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Link
                            :href="route('settings.maintenance.sla-policies.index')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600"
                        >
                            <span v-if="form.processing">Updating...</span>
                            <span v-else>Update SLA Policy</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policy: Object,
    serviceCategories: Array,
});

const toast = useToast();

const form = useSecureForm({
    name: props.policy.name,
    description: props.policy.description,
    work_order_type: props.policy.work_order_type || '',
    service_category_id: props.policy.service_category_id || '',
    service_id: props.policy.service_id || '',
    priority: props.policy.priority || '',
    response_time_hours: props.policy.response_time_hours || '',
    resolution_time_hours: props.policy.resolution_time_hours || '',
    is_active: props.policy.is_active,
});

const availableServices = computed(() => {
    if (!form.service_category_id) return [];
    const category = props.serviceCategories.find(c => c.id === form.service_category_id);
    return category?.services || [];
});

const onCategoryChange = () => {
    // Only clear service if it's not in the new category's services
    if (form.service_id) {
        const serviceExists = availableServices.value.some(s => s.id === form.service_id);
        if (!serviceExists) {
            form.service_id = '';
        }
    }
};

const submit = () => {
    form.put(route('settings.maintenance.sla-policies.update', props.policy.id), {
        onError: () => {
            toast.error('Failed to update SLA policy. Please check the form for errors.');
        }
    });
};
</script>
